import { t } from "@lingui/core/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Clock } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { SendCommitRequest } from "@/api";
import { DynamicForm } from "@/components/forms/DynamicForm";
import { RampsButton } from "@/components/ramps/Button";
import { RampsSelectBottomSheet } from "@/components/ramps/RampsSelectBottomSheet";
import { RampsText } from "@/components/ramps/Text";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { BANK_SERVICE_OPTIONS } from "@/domain/calculator";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useContinue } from "@/hooks/useContinue";
import { useNavigateBack } from "@/hooks/useNavigateBack";
import { useAnalytics } from "@/hooks/usePostHog";
import { getAuthTokenFromSearchParams } from "@/lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "@/stores/use-ramps-store";
import { getActiveUserLanguageTag } from "@/utils/user-language";
import { Progress } from "../components/ui/progress";

function DigitalOfframpInfoPage() {
	const search = Route.useSearch();
	const store = useRampsStore();
	const handleBack = useNavigateBack();
	const handleContinue = useContinue();
	const { trackPageView } = useAnalytics();
	// Get auth token (supports both v2 and v5 API)
	const authToken = getAuthTokenFromSearchParams(search) || store.token;

	// Track page view
	useEffect(() => {
		trackPageView("digital-offramp-info", {
			page_name: "digital-offramp-info",
		});
	}, [trackPageView]);
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
	});

	// Extract bank service options from quote response
	const bankDepositOptions = useMemo(() => {
		// Return empty array if serviceOption is not a bank service option
		if (
			!store.serviceOption ||
			!(BANK_SERVICE_OPTIONS as readonly string[]).includes(store.serviceOption)
		) {
			return [];
		}
		return (
			store.quoteResponse?.transactions.filter(
				(tx) => tx.serviceOptionCode === store.serviceOption,
			) || []
		);
	}, [store.quoteResponse, store.serviceOption]);

	// Phase 2: Track selection state
	const [selectedServiceOption, setSelectedServiceOption] =
		useState<string>("");
	const [selectedBankCode, setSelectedBankCode] = useState<string>("");

	// Phase 4: Helper to detect "All banks" or "All wallets" option
	const isAllBanksOption = useCallback(
		(routingCode: string): boolean => {
			const option = bankDepositOptions.find(
				(tx) => tx.serviceOptionRoutingCode === routingCode,
			);
			return (
				["all banks", "other banks", "all wallets"].includes(
					option?.serviceOptionRoutingName?.toLowerCase() ?? "",
				) || false
			);
		},
		[bankDepositOptions],
	);

	// Initialize from store if routing code already set
	useEffect(() => {
		if (store.serviceOptionRoutingCode && !selectedServiceOption) {
			setSelectedServiceOption(store.serviceOptionRoutingCode);
		}
	}, [store.serviceOptionRoutingCode, selectedServiceOption]);

	// Initialize selectedBankCode from store if selectedBankChoice exists
	useEffect(() => {
		if (store.selectedBankChoice && !selectedBankCode) {
			setSelectedBankCode(store.selectedBankChoice.attrValue);
		}
	}, [store.selectedBankChoice, selectedBankCode]);

	// Auto-select if only one bank service option available
	useEffect(() => {
		if (
			bankDepositOptions.length === 1 &&
			!store.serviceOptionRoutingCode &&
			!store.bankFieldsGFFP?.values
		) {
			const singleOption = bankDepositOptions[0];
			if (singleOption.serviceOptionRoutingCode) {
				// Check if this is an "All Banks" or "All Wallets" option
				if (isAllBanksOption(singleOption.serviceOptionRoutingCode)) {
					// Set selectedServiceOption to trigger payoutEntitiesQuery
					// We'll decide whether to auto-select or show dropdown based on the response
					setSelectedServiceOption(singleOption.serviceOptionRoutingCode);
				} else {
					// Not "All Banks"/"All Wallets" - proceed as before (Case 3 behavior)
					rampsActions.setServiceOptionRoutingCode(
						singleOption.serviceOptionRoutingCode,
					);
				}
			}
		}
	}, [
		bankDepositOptions,
		store.serviceOptionRoutingCode,
		store.bankFieldsGFFP,
		isAllBanksOption,
	]);

	// Skip selectors if routing code already set or in edit mode
	const shouldShowSelectors =
		!store.serviceOptionRoutingCode &&
		!store.bankFieldsGFFP?.values &&
		bankDepositOptions.length > 0;

	// Phase 3: Create payout entities query (conditional)
	const payoutEntitiesQuery = useQuery({
		queryKey: [
			"payoutEntities",
			store.payoutCountry,
			store.state,
			store.amount,
			store.currency,
			store.payoutCurrency,
			selectedServiceOption,
			store.serviceOption,
			// Backend returns localized payout-entity labels keyed off
			// userLanguage. Include the active locale in the cache key so a
			// post-bootstrap language change refetches instead of serving
			// stale strings from the en-US response.
			store.lang,
		],
		queryFn: async () => {
			const sendCountry = store.residencyCountry || "USA"; // Default to USA if not set
			// Use different deliveryOptionId based on service option:
			// - "10" for BANK_DEPOSIT
			// - "19" for DIRECT_TO_ACCT
			const deliveryOptionId =
				store.serviceOption === "DIRECT_TO_ACCT" ? "19" : "10";
			return await apiClient.referenceData.getPayoutEntities({
				sendCountry: sendCountry,
				destinationCountry: store.payoutCountry, // Country CODE (e.g., "COL")
				deliveryOptionId,
				receiveCurrency: store.payoutCurrency,
				receiveAgentId: selectedServiceOption, // The "all banks" routing code
				sendCurrencyCode: store.currency,
				userLanguage: getActiveUserLanguageTag(),
			});
		},
		enabled: !!(
			authToken &&
			selectedServiceOption &&
			isAllBanksOption(selectedServiceOption) &&
			bankDepositOptions.length >= 1 && // Changed from > 1 to support Cases 1 and 2
			store.payoutCountry &&
			store.amount &&
			store.currency &&
			store.payoutCurrency
		),
		retry: 1,
	});

	// Case 1: Auto-select single bank when payout-entities returns only 1 choice
	useEffect(() => {
		if (
			bankDepositOptions.length === 1 &&
			selectedServiceOption &&
			isAllBanksOption(selectedServiceOption) &&
			!selectedBankCode &&
			!store.serviceOptionRoutingCode &&
			payoutEntitiesQuery.data &&
			!store.bankFieldsGFFP?.values
		) {
			const allChoices =
				payoutEntitiesQuery.data.destinationEntityGroupHeading?.flatMap(
					(group) => group.choices,
				) || [];

			if (allChoices.length === 1) {
				// Auto-select the single bank and proceed to bank fields
				const singleBank = allChoices[0];
				setSelectedBankCode(singleBank.attrValue);
				rampsActions.setSelectedBankChoice(singleBank);
			}
			// If allChoices.length > 1, Case 2 will handle showing the dropdown
		}
	}, [
		bankDepositOptions,
		selectedServiceOption,
		selectedBankCode,
		store.serviceOptionRoutingCode,
		store.bankFieldsGFFP,
		payoutEntitiesQuery.data,
		isAllBanksOption,
	]);

	// Mutation for saving bank account to profile
	const saveBankAccountMutation = useMutation({
		mutationKey: ["saveBankAccount"],
		mutationFn: async (params: {
			digitalProfileId: string;
			saveRequest: import("@/api").SaveBankAccountRequest;
		}) => {
			return await apiClient.profile.saveBankAccount(
				params.digitalProfileId,
				params.saveRequest,
			);
		},
		onSuccess: () => {
			// Bank account saved successfully
		},
		onError: () => {
			// Don't throw - allow flow to continue even if save fails
		},
	});

	// Phase 5: Fetch bank fields on page load (updated to wait for selection)
	const bankFieldsQuery = useQuery({
		queryKey: [
			"bankFields",
			store.payoutCountry,
			store.state,
			store.amount,
			store.currency,
			store.payoutCurrency,
			store.serviceOptionRoutingCode,
			// Localized field labels — see payoutEntities query above for rationale.
			store.lang,
		],
		queryFn: async () => {
			const response = await apiClient.gffpBankFields.getBankFields({
				destinationCountryCode: store.payoutCountry,
				destinationCountrySubdivisionCode: store.state || undefined,
				sendAmount: Number.parseFloat(store.amount),
				sendCurrencyCode: store.currency,
				receiveCurrencyCode: store.payoutCurrency,
				serviceOptionCode: store.serviceOption as
					| "BANK_DEPOSIT"
					| "DIRECT_TO_ACCT",
				serviceOptionRoutingCode: store.serviceOptionRoutingCode,
				userLanguage: getActiveUserLanguageTag(),
			});
			return response;
		},
		enabled: !!(
			authToken &&
			store.payoutCountry &&
			store.amount &&
			store.currency &&
			store.payoutCurrency &&
			store.serviceOptionRoutingCode &&
			// Case 1: Single option + "All Banks/Wallets" + single bank = auto-selected (proceed)
			// Case 2: Single option + "All Banks/Wallets" + multiple banks = wait for bank selection
			// Case 3: Multiple options = existing behavior

			// Non "All Banks/Wallets" options - proceed immediately
			(!isAllBanksOption(store.serviceOptionRoutingCode) ||
				// "All Banks/Wallets" with bank selected (covers Case 2 after selection)
				selectedBankCode ||
				// Single option that was auto-selected (Case 1 or non-"All Banks/Wallets" single option)
				(bankDepositOptions.length === 1 && store.serviceOptionRoutingCode))
		),
		retry: 1,
	});

	// Format fields from dash notation back to dot notation for storage
	const formatFieldsWithDotNotation = (fields: Record<string, unknown>) => {
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(fields)) {
			// Convert dashes to dots in the key
			const dotNotationKey = key.replace(/-/g, ".");
			result[dotNotationKey] = value;
		}

		return result;
	};

	const handleFormSubmit = async (data: Record<string, unknown>) => {
		if (!data) return;

		// Format the data back to dot notation
		const formattedFormData = formatFieldsWithDotNotation(data);

		// Store both schema and values
		if (bankFieldsQuery.data?.fieldInfo) {
			rampsActions.setBankFieldsGFFP(
				bankFieldsQuery.data.fieldInfo,
				formattedFormData,
			);
		}

		// If we have a validation payload (editing scenario), merge bank fields and re-validate
		// BUT: If user has profile and cleared their bank account, rebuild from scratch
		const isUserEditingBankAccount =
			store.digitalProfileId && !store.selectedBankAccount;

		if (store.validatePayload && !isUserEditingBankAccount) {
			const deepMerge = (
				target: Record<string, unknown>,
				source: Record<string, unknown>,
			): Record<string, unknown> => {
				const result = { ...target };
				for (const [key, value] of Object.entries(source)) {
					if (value && typeof value === "object" && !Array.isArray(value)) {
						if (
							result[key] &&
							typeof result[key] === "object" &&
							!Array.isArray(result[key])
						) {
							result[key] = deepMerge(
								result[key] as Record<string, unknown>,
								value as Record<string, unknown>,
							);
						} else {
							result[key] = value;
						}
					} else {
						result[key] = value;
					}
				}
				return result;
			};

			// Convert dot notation to nested structure (like additional-info does)
			const formatFieldsToDeeplyNested = (
				flatData: Record<string, unknown>,
			) => {
				const result: Record<string, unknown> = {};
				for (const [key, value] of Object.entries(flatData)) {
					const keys = key.split(".");
					let current = result;
					for (let i = 0; i < keys.length - 1; i++) {
						const currentKey = keys[i];
						if (!(currentKey in current)) {
							current[currentKey] = {};
						}
						current = current[currentKey] as Record<string, unknown>;
					}
					current[keys[keys.length - 1]] = value;
				}
				return result;
			};

			const nestedBankFields = formatFieldsToDeeplyNested(formattedFormData);
			const updatedPayload = deepMerge(
				store.validatePayload as unknown as Record<string, unknown>,
				nestedBankFields,
			) as unknown as SendCommitRequest;

			// Re-validate with merged payload
			rampsActions.setValidatePayload(updatedPayload);
			// Determine operation: use store.operation if set, otherwise infer from cashInLocation
			const operation =
				store.operation || (store.cashInLocation ? "deposit" : "withdraw");
			const validationResponse = await apiClient.sendCommit.validateTransaction(
				store.transactionId,
				updatedPayload,
				{ params: { operation } },
			);
			rampsActions.setTransactionResponse(validationResponse);

			// If validation successful and user has profile and is editing account, save to profile
			// Do not save DIRECT_TO_ACCT options as bank accounts - they are one-time transfers
			if (
				validationResponse.readyForCommit &&
				isUserEditingBankAccount &&
				store.digitalProfileId &&
				store.serviceOption !== "DIRECT_TO_ACCT"
			) {
				// Extract ALL bank account fields dynamically from GFFP data
				const extractBankFieldsFromData = (
					bankData: Record<string, unknown>,
				) => {
					const targetAccountFields: Record<string, string> = {};

					Object.entries(bankData).forEach(([key, value]) => {
						if (key.startsWith("targetAccount.")) {
							const cleanKey = key.replace("targetAccount.", "");
							targetAccountFields[cleanKey] = String(value);
						}
					});

					return targetAccountFields;
				};

				const extractedFields = extractBankFieldsFromData(formattedFormData);

				if (
					extractedFields &&
					Object.keys(extractedFields).length > 0 &&
					validationResponse.targetAccountProfileId
				) {
					await saveBankAccountMutation.mutateAsync({
						digitalProfileId: store.digitalProfileId,
						saveRequest: {
							targetAccountProfileId: validationResponse.targetAccountProfileId,
							targetAccount: extractedFields,
						},
					});
				}
			}
		}

		// Navigate to next step
		handleContinue(search);
	};

	// Check if we're in edit mode (coming from review)
	const isEditMode = !!store.bankFieldsGFFP?.values;

	// Loading state
	if (bankFieldsQuery.isLoading) {
		return <LoadingSpinner size="lg" />;
	}

	// Error state
	if (bankFieldsQuery.isError) {
		return (
			<div className="flex min-h-dvh flex-col bg-ramps-background-base">
				{/* Header */}
				<div className="flex items-center justify-between p-4">
					<button
						type="button"
						onClick={handleBack}
						className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
						aria-label="Back"
					>
						<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
					</button>
				</div>

				{/* Progress bar */}
				<div className="mb-8 px-4">
					<Progress
						value={72}
						className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
					/>
				</div>

				{/* Error content */}
				<div className="flex flex-1 flex-col items-center justify-center px-4">
					<RampsText variant="title-lg-bold" color="primary" className="mb-4">
						{store.serviceOption === "DIRECT_TO_ACCT"
							? t`Unable to load account fields`
							: t`Unable to load bank account fields`}
					</RampsText>
					<RampsText
						variant="body-default"
						color="secondary"
						className="mb-6 text-center"
					>
						{t`There was an error loading the required fields. Please try again.`}
					</RampsText>
					<button
						type="button"
						onClick={() => bankFieldsQuery.refetch()}
						className="rounded-lg bg-ramps-text-primary px-6 py-3 text-white transition-opacity hover:opacity-90"
					>
						{t`Retry`}
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className="flex min-h-dvh flex-col bg-ramps-background-base">
			{/* Header */}
			<div className="flex items-center justify-between p-4">
				<button
					type="button"
					onClick={handleBack}
					className="flex h-8 w-8 items-center justify-center transition-opacity hover:opacity-80"
					aria-label="Back"
				>
					<ArrowLeft className="h-6 w-6 text-ramps-text-primary" />
				</button>
			</div>

			{/* Progress bar */}
			<div className="mb-8 px-4">
				<Progress
					value={72}
					className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
				/>
			</div>

			{/* Content */}
			<div className="flex flex-1 flex-col px-4">
				<div className="mb-8 text-left">
					<RampsText variant="title-lg-bold" color="primary" className="mb-2">
						{store.serviceOption === "DIRECT_TO_ACCT"
							? t`Account Information`
							: t`Bank Account Information`}
					</RampsText>
					<div className="text-muted-foreground text-sm">
						{store.serviceOption === "DIRECT_TO_ACCT"
							? t`Please provide your account details to receive your funds.`
							: t`Please provide your bank account details to receive your funds.`}
					</div>
				</div>

				{/* Phase 6: Service Option Selector */}
				{shouldShowSelectors && bankDepositOptions.length > 1 && (
					<div className="mb-6">
						<RampsSelectBottomSheet
							label={
								store.serviceOption === "DIRECT_TO_ACCT"
									? t`Select your account`
									: t`Select your bank`
							}
							sheetTitle={
								store.serviceOption === "DIRECT_TO_ACCT"
									? t`Select your account`
									: t`Select your bank`
							}
							options={bankDepositOptions.map((tx) => ({
								value: tx.serviceOptionRoutingCode || "",
								label:
									tx.serviceOptionRoutingName ||
									tx.serviceOptionRoutingCode ||
									"",
							}))}
							value={selectedServiceOption}
							onSelect={(value) => {
								setSelectedServiceOption(value);
								setSelectedBankCode(""); // Reset bank selection
							}}
							size="compact"
							placeholder={t`Choose an option`}
						/>
					</div>
				)}

				{/* Phase 6: Bank Selector - Case 2: Show for "All banks" with multiple banks */}
				{shouldShowSelectors &&
					selectedServiceOption &&
					isAllBanksOption(selectedServiceOption) &&
					!selectedBankCode && (
						<div className="mb-6">
							{payoutEntitiesQuery.isLoading && (
								<div className="flex items-center justify-center py-4">
									<LoadingSpinner size="sm" />
								</div>
							)}
							{payoutEntitiesQuery.isError && (
								<div className="mb-2 text-center text-red-500 text-sm">
									{store.serviceOption === "DIRECT_TO_ACCT"
										? t`Error loading accounts. Please try again.`
										: t`Error loading banks. Please try again.`}
								</div>
							)}
							{payoutEntitiesQuery.data?.destinationEntityGroupHeading &&
								payoutEntitiesQuery.data.destinationEntityGroupHeading.length >
									0 &&
								payoutEntitiesQuery.data.destinationEntityGroupHeading.flatMap(
									(group) => group.choices,
								).length > 1 && (
									<RampsSelectBottomSheet
										label={
											store.serviceOption === "DIRECT_TO_ACCT"
												? t`Select Your Account`
												: t`Select Your Bank`
										}
										sheetTitle={
											store.serviceOption === "DIRECT_TO_ACCT"
												? t`Select Your Account`
												: t`Select Your Bank`
										}
										options={payoutEntitiesQuery.data.destinationEntityGroupHeading
											.flatMap((group) => group.choices)
											.sort((a, b) => a.displayOrder - b.displayOrder)
											.map((choice) => ({
												value: choice.attrValue,
												label: choice.choiceName,
											}))}
										value={selectedBankCode}
										onSelect={(value) => {
											const allChoices =
												payoutEntitiesQuery.data?.destinationEntityGroupHeading?.flatMap(
													(group) => group.choices,
												) || [];
											const selectedChoice = allChoices.find(
												(choice) => choice.attrValue === value,
											);

											if (selectedChoice) {
												setSelectedBankCode(value);
												rampsActions.setSelectedBankChoice(selectedChoice);
											}
										}}
										size="full"
										searchPlaceholder={
											store.serviceOption === "DIRECT_TO_ACCT"
												? t`Search accounts...`
												: t`Search banks...`
										}
										placeholder={
											store.serviceOption === "DIRECT_TO_ACCT"
												? t`Choose your account`
												: t`Choose your bank`
										}
									/>
								)}
							{payoutEntitiesQuery.data &&
								payoutEntitiesQuery.data.destinationEntityGroupHeading
									?.length === 0 && (
									<div className="py-4 text-center text-sm">
										<RampsText variant="body-default" color="secondary">
											{store.serviceOption === "DIRECT_TO_ACCT"
												? t`No accounts available for this option.`
												: t`No banks available for this option.`}
										</RampsText>
									</div>
								)}
						</div>
					)}

				{/* Phase 6: Confirmation button for non-"All banks" options */}
				{shouldShowSelectors &&
					selectedServiceOption &&
					!isAllBanksOption(selectedServiceOption) &&
					bankDepositOptions.length > 1 && (
						<div className="mb-6">
							<RampsButton
								onClick={() => {
									rampsActions.setServiceOptionRoutingCode(
										selectedServiceOption,
									);
								}}
								className="w-full"
							>
								{t`Continue`}
							</RampsButton>
						</div>
					)}

				{/* Dynamic Form - only show when routing code is set */}
				{store.serviceOptionRoutingCode && (
					<DynamicForm
						fields={bankFieldsQuery.data?.fieldInfo || []}
						initialValues={
							isEditMode && store.bankFieldsGFFP
								? store.bankFieldsGFFP.values
								: undefined
						}
						onSubmit={handleFormSubmit}
						className="bg-card"
						reminder={{
							header: t`Processing Time`,
							text: t`Bank transfers typically take 1-3 business days to complete`,
							icon: Clock,
						}}
					/>
				)}
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/digital-offramp-info")({
	component: DigitalOfframpInfoPage,
});
